import React, { useState, useRef, useEffect, useCallback } from 'react';
import { galleryPhotos } from '../data/gallery-images';

const Gallery: React.FC = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const handleScroll = useCallback(() => {
        if (isDragging) return; // Don't update index while dragging
        if (scrollContainerRef.current) {
            const currentScrollLeft = scrollContainerRef.current.scrollLeft;
            const itemWidth = scrollContainerRef.current.scrollWidth / galleryPhotos.length;
            const newIndex = Math.round(currentScrollLeft / itemWidth);
            setActiveIndex(newIndex);
        }
    }, [isDragging]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll, { passive: true });
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    const onMouseDown = (e: React.MouseEvent) => {
        if (scrollContainerRef.current && e.button === 0) {
            setIsDragging(true);
            setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
            setScrollLeft(scrollContainerRef.current.scrollLeft);
            scrollContainerRef.current.style.cursor = 'grabbing';
            scrollContainerRef.current.style.scrollSnapType = 'none'; // Disable snap during drag
        }
    };

    useEffect(() => {
        const container = scrollContainerRef.current;

        const onMouseMove = (e: MouseEvent) => {
            if (!isDragging || !container) return;
            e.preventDefault();
            const x = e.pageX - container.offsetLeft;
            const walk = (x - startX);
            container.scrollLeft = scrollLeft - walk;
        };

        const onMouseUp = () => {
            if (isDragging && container) {
                setIsDragging(false);
                container.style.cursor = 'grab';
                container.style.scrollSnapType = 'x mandatory'; // Re-enable snap
                // Recalculate index after dragging ends
                handleScroll();
            }
        };

        if (isDragging) {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        } else {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging, startX, scrollLeft, handleScroll]);

    const renderMediaItem = (item: any, index: number) => {
        if (item.type === 'video') {
            return (
                <video
                    src={`/Gallery/${item.src}`}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                    style={{ transform: `rotate(${item.rotation || 0}deg)` }}
                    onMouseDown={(e) => e.preventDefault()} // Prevents dragging interference
                />
            );
        } else {
            return (
                <img
                    src={`/Gallery/${item.src}`}
                    alt={`Gallery entry ${index + 1}`}
                    className="w-full h-full object-cover"
                    style={{ transform: `rotate(${item.rotation || 0}deg)` }}
                    onDragStart={(e) => e.preventDefault()} // Prevents native image dragging
                />
            );
        }
    };

    return (
        <div className="w-full">
            <div
                ref={scrollContainerRef}
                className="flex overflow-x-auto scrollbar-hide cursor-grab"
                onMouseDown={onMouseDown}
            >
                {galleryPhotos.map((photo, index) => (
                    <div
                        key={index}
                        className={`flex-shrink-0 w-[calc(100%/1.25)] md:w-[calc(100%/4.25)] ${photo.className}`}
                    >
                        {renderMediaItem(photo, index)}
                    </div>
                ))}
            </div>
            <div className="flex justify-center mt-4 space-x-2 md:hidden">
                {galleryPhotos.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => {
                            if (scrollContainerRef.current) {
                                const itemWidth = scrollContainerRef.current.scrollWidth / galleryPhotos.length;
                                scrollContainerRef.current.scrollTo({
                                    left: itemWidth * index,
                                    behavior: 'smooth'
                                });
                            }
                        }}
                        className={`w-2 h-2 rounded-full ${activeIndex === index ? 'bg-(--hover-color)' : 'bg-gray-400'}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default Gallery;